import React, { useCallback, useEffect, useState } from "react";
import { ChevronRight, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Panel, borderStyle, inputCls } from "../ui";
import { PageLoader } from "../../components/States";

/** Flatten the menu tree into rows so the editor stays a simple list. */
function flatten(nodes, path = [], out = []) {
  nodes.forEach((n, i) => {
    const p = path.concat(i);
    out.push({ node: n, path: p, depth: p.length - 1 });
    flatten(n.children || [], p, out);
  });
  return out;
}

function nodeAt(tree, path) {
  let list = tree;
  let node = null;
  for (let i = 0; i < path.length; i += 1) {
    node = list[path[i]];
    if (!node) return null;
    if (i < path.length - 1) list = node.children || [];
  }
  return node;
}

export default function NavAdmin() {
  const [nav, setNav] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi
      .get("/admin/nav")
      .then((r) => setNav(r.data.nav))
      .catch(() => setNav([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = (fn) =>
    setNav((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      fn(copy);
      return copy;
    });

  const change = (path, patch) =>
    mutate((t) => {
      const n = nodeAt(t, path);
      if (n) Object.assign(n, patch);
    });

  const addChild = (path) =>
    mutate((t) => {
      const n = nodeAt(t, path);
      if (!n) return;
      if (!n.children) n.children = [];
      n.children.push({ label: "New item", slug: "", href: "", children: [] });
    });

  const remove = (path) =>
    mutate((t) => {
      const parentPath = path.slice(0, -1);
      const idx = path[path.length - 1];
      const list = parentPath.length ? (nodeAt(t, parentPath) || {}).children : t;
      if (list) list.splice(idx, 1);
    });

  const addTop = () =>
    setNav((n) => n.concat([{ label: "New section", slug: "", href: "", children: [] }]));

  async function save() {
    setBusy(true);
    try {
      await adminApi.put("/admin/nav", { nav });
      toast.success("Navigation saved");
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  }

  if (!nav) return <PageLoader />;

  const rows = flatten(nav);

  return (
    <div data-testid="admin-nav-editor">
      <AdminHeader
        title="Navigation"
        subtitle={`${rows.length} menu items across 3 levels`}
      >
        <button type="button" onClick={addTop} className="btn-outline-nq" data-testid="nav-add-top-button">
          <Plus className="h-4 w-4" /> Add section
        </button>
        <button type="button" onClick={save} disabled={busy} className="btn-gold" data-testid="nav-save-button">
          <Save className="h-4 w-4" /> {busy ? "Saving..." : "Save navigation"}
        </button>
      </AdminHeader>

      <Panel>
        <div className="space-y-2">
          {rows.map((row) => {
            const key = row.path.join("-");
            return (
              <div
                key={key}
                className="flex flex-wrap items-center gap-2 rounded-lg border p-2"
                style={{
                  borderColor: "rgba(255,255,255,0.1)",
                  marginLeft: row.depth * 20,
                }}
                data-testid="nav-row"
              >
                <ChevronRight className="h-3.5 w-3.5 gold-text" />
                <input
                  value={row.node.label || ""}
                  onChange={(e) => change(row.path, { label: e.target.value })}
                  placeholder="Menu label"
                  className={inputCls}
                  style={{ ...borderStyle, maxWidth: 240 }}
                  data-testid="nav-label-input"
                />
                <input
                  value={row.node.slug || ""}
                  onChange={(e) => change(row.path, { slug: e.target.value })}
                  placeholder="page-slug"
                  className={`${inputCls} font-mono text-xs`}
                  style={{ ...borderStyle, maxWidth: 240 }}
                  data-testid="nav-slug-input"
                />
                <button
                  type="button"
                  onClick={() => addChild(row.path)}
                  className="btn-outline-nq px-2 py-1 text-xs"
                  data-testid="nav-add-child-button"
                >
                  <Plus className="h-3 w-3" /> child
                </button>
                <button
                  type="button"
                  onClick={() => remove(row.path)}
                  className="rounded p-1.5 hover:bg-white/10"
                  data-testid="nav-remove-button"
                >
                  <Trash2 className="h-3.5 w-3.5" style={{ color: "#e07a72" }} />
                </button>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
